/**
 * Parses a date-only ISO string (YYYY-MM-DD) as UTC midnight.
 * Using `new Date("YYYY-MM-DD")` is ambiguous across browsers — some parse it
 * as local time, others as UTC — which causes off-by-one-day errors for users
 * in UTC+ timezones. This function always yields UTC midnight.
 */
export const parseDateUTC = (isoDateString) => {
  const [ year, month, day ] = isoDateString.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

/** Returns the number of days in a given UTC year/month (month is 0-indexed). */
const getDaysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

/**
 * Translates a time series to a new starting point
 * @param isoTimestampStrings
 * @param newStartingDate
 * @returns The newly translated Date objects
 */
export const translateTimeSeries = (isoTimestampStrings, newStartingDate) => {
  const sortedDates = isoTimestampStrings
    .map(iso => parseDateUTC(iso))
    .sort((a, b) => a - b)

  const baseTime = sortedDates[0].getTime()
  const referenceTime = newStartingDate.getTime()

  return sortedDates.map(d => new Date(referenceTime + (d - baseTime)))
}

/**
 * Given a past date, finds the closest occurrence of the same day-of-week
 * to the next available same calendar date (month + day) starting from `today`.
 *
 * Example: March 3rd (Tuesday) → find next available March 3rd → find the
 * Tuesday closest to that date.
 *
 * Leap year: if the original date is Feb 29 and the target year is not a leap
 * year, Feb 28 is used as the anchor before finding the closest weekday.
 *
 * If the closest weekday resolves to a date before `today` (due to a negative
 * offset from the anchor), it advances by one week.
 */
export const calculateFutureEquivalentDate = (pastDate, today) => {
  const dayOfWeek = pastDate.getUTCDay()
  const month = pastDate.getUTCMonth()
  const calendarDay = pastDate.getUTCDate()
  const isLeapDay = month === 1 && calendarDay === 29

  const anchorDayForYear = (year) => isLeapDay && getDaysInMonth(year, 1) < 29 ? 28 : calendarDay

  let targetYear = today.getUTCFullYear()
  const todayMonth = today.getUTCMonth()
  const todayDay = today.getUTCDate()

  let anchorDay = anchorDayForYear(targetYear)
  if (month < todayMonth || (month === todayMonth && anchorDay <= todayDay)) {
    targetYear++
    anchorDay = anchorDayForYear(targetYear)
  }

  const anchorDayOfWeek = new Date(Date.UTC(targetYear, month, anchorDay)).getUTCDay()

  // Normalize diff to [-3, +3] — always a unique closest weekday
  let diff = dayOfWeek - anchorDayOfWeek
  if (diff > 3) {
    diff -= 7
  }
  if (diff < -3) {
    diff += 7
  }

  const result = new Date(Date.UTC(targetYear, month, anchorDay + diff))

  // If a negative offset pushed the result before today, advance one week
  if (result < today) {
    return new Date(result.getTime() + 7 * 24 * 60 * 60 * 1000)
  }

  return result
}

/** Returns today at UTC midnight */
export const todayUTC = () => {
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

/** Formats a Date to YYYY-MM-DD using UTC components */
export const toISODateString = (date) => date.toISOString().split('T')[0]
